import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Calendar, Clock, Mail, Shield } from 'lucide-react';
import { getAvailability, getSiteSettings } from '../services/api';
import { useRecaptcha } from '../contexts/RecaptchaContext';
import { toast } from 'sonner';

export const AppointmentBooking = ({ propertyId, propertyTitle }) => {
  const [availability, setAvailability] = useState(null);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    tenant_name: '',
    tenant_email: '',
    tenant_phone: '',
    requested_date: '',
    requested_time: '',
    notes: ''
  });
  const { executeRecaptcha, enabled: recaptchaEnabled } = useRecaptcha();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [avail, siteSettings] = await Promise.all([
        getAvailability(),
        getSiteSettings()
      ]);
      setAvailability(avail);
      setSettings(siteSettings);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const getAvailableDays = () => {
    if (!availability) return [];
    return availability.schedule
      .filter(day => day.enabled)
      .map(day => day.day);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.tenant_name || !formData.tenant_email || !formData.tenant_phone || 
        !formData.requested_date || !formData.requested_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Execute reCAPTCHA if enabled
    if (recaptchaEnabled) {
      try {
        const token = await executeRecaptcha('appointment');
        if (!token) {
          toast.error('Security verification failed. Please try again.');
          return;
        }
        // Token will be sent with the email for verification tracking
        console.log('✅ reCAPTCHA verified');
      } catch (error) {
        toast.error('Security verification failed. Please refresh and try again.');
        return;
      }
    }

    // Create mailto: link with appointment details
    const appointmentEmail = settings?.appointments_email || settings?.email || 'info@cozyproperties.com';
    const subject = encodeURIComponent(`Viewing Request: ${propertyTitle}`);
    const body = encodeURIComponent(
      `I would like to schedule a viewing for: ${propertyTitle}\n\n` +
      `Preferred Date: ${formData.requested_date}\n` +
      `Preferred Time: ${formData.requested_time}\n\n` +
      `Contact Information:\n` +
      `Name: ${formData.tenant_name}\n` +
      `Email: ${formData.tenant_email}\n` +
      `Phone: ${formData.tenant_phone}\n\n` +
      `${formData.notes ? `Additional Notes:\n${formData.notes}` : ''}`
    );
    
    const mailtoLink = `mailto:${appointmentEmail}?subject=${subject}&body=${body}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    toast.success('Opening your email client...');
  };

  const availableDays = getAvailableDays();
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  // Get minimum date (today)
  const today = new Date();
  const minDate = today.toISOString().split('T')[0];

  // Get maximum date (3 months from now)
  const maxDate = new Date(today.setMonth(today.getMonth() + 3)).toISOString().split('T')[0];

  return (
    <Card className="dark:bg-gray-900 dark:border-gray-800">
      <CardHeader>
        <CardTitle className="flex items-center dark:text-white">
          <Calendar className="h-5 w-5 mr-2" />
          Schedule a Viewing
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="tenant_name">Your Name *</Label>
            <Input
              id="tenant_name"
              value={formData.tenant_name}
              onChange={(e) => setFormData({ ...formData, tenant_name: e.target.value })}
              required
              
            />
          </div>

          <div>
            <Label htmlFor="tenant_email">Email Address *</Label>
            <Input
              id="tenant_email"
              type="email"
              value={formData.tenant_email}
              onChange={(e) => setFormData({ ...formData, tenant_email: e.target.value })}
              required
              
            />
          </div>

          <div>
            <Label htmlFor="tenant_phone">Phone Number *</Label>
            <Input
              id="tenant_phone"
              type="tel"
              value={formData.tenant_phone}
              onChange={(e) => setFormData({ ...formData, tenant_phone: e.target.value })}
              required
              
            />
          </div>

          <div>
            <Label htmlFor="requested_date">Preferred Date *</Label>
            <Input
              id="requested_date"
              type="date"
              value={formData.requested_date}
              onChange={(e) => setFormData({ ...formData, requested_date: e.target.value })}
              min={minDate}
              max={maxDate}
              required
              
            />
            {availableDays.length > 0 && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Available days: {availableDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="requested_time">Preferred Time *</Label>
            <Input
              id="requested_time"
              type="time"
              value={formData.requested_time}
              onChange={(e) => setFormData({ ...formData, requested_time: e.target.value })}
              required
              
            />
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Typical hours: 9:00 AM - 5:00 PM
            </p>
          </div>

          <div>
            <Label htmlFor="notes">Additional Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any specific requirements or questions..."
              rows={3}
              
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-otter-green hover:bg-otter-green-600 text-white"
          >
            <Mail className="h-4 w-4 mr-2" />
            Send Appointment Request
          </Button>

          <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
            This will open your email client with a pre-filled message
          </p>

          {recaptchaEnabled && (
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <Shield className="h-3 w-3" />
              <span>Protected by reCAPTCHA</span>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
